import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { requireGroupId, extractError } from '../../store/thunkHelpers';
import { whatsAppSessionApi } from './whatsAppSessionApi';
import { personApi } from '../person/personApi';
import type { Person } from '../person/personTypes';
import type { WhatsAppConversation, WhatsAppSession } from './whatsAppSessionTypes';

/**
 * Argomento di {@link fetchWhatsAppSessions}.
 *
 * `silent` distingue il caricamento voluto dall'utente dal ricarico periodico del
 * polling (vedi useWhatsAppSessionPolling): quando è true lo slice aggiorna i dati
 * ma NON tocca `status`/`error`. Senza questa distinzione un tick ogni pochi secondi
 * farebbe sfarfallare lo stato della slice, e un singolo tick andato male la
 * lascerebbe su 'failed' pur avendo in cache dati validi.
 */
export interface FetchWhatsAppSessionsArg {
  personId: number;
  silent?: boolean;
}

/**
 * Carica le sessioni di conversazione WhatsApp di un contatto (con i messaggi
 * annidati). Il risultato è indicizzato per personId nello slice.
 *
 * Il `groupId` lo risolve `requireGroupId` dallo stato, come fanno tutti gli altri
 * thunk del progetto: prenderlo come argomento costringeva ogni chiamante a
 * procurarselo, ed era la causa prossima di una GET verso `/whatsapp-sessions/0/{id}`
 * al primo render, quando il groupId non era ancora nello store.
 */
export const fetchWhatsAppSessions = createAsyncThunk<
  { personId: number; sessions: WhatsAppSession[] },
  FetchWhatsAppSessionsArg,
  { state: RootState; rejectValue: string }
>(
  'whatsAppSessions/fetchByPerson',
  async ({ personId }, { getState, rejectWithValue }) => {
    try {
      const sessions = await whatsAppSessionApi.getByPerson(
        requireGroupId(getState),
        personId,
      );
      return { personId, sessions };
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Elimina una sessione (e a cascata i suoi messaggi lato backend). Ritorna l'id
 * eliminato così lo slice la rimuove dalla cache byPersonId.
 */
export const deleteWhatsAppSession = createAsyncThunk<
  number,
  number,
  { state: RootState; rejectValue: string }
>('whatsAppSessions/delete', async (whatsAppSessionId, { rejectWithValue }) => {
  try {
    await whatsAppSessionApi.remove(whatsAppSessionId);
    return whatsAppSessionId;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Le conversazioni della pagina WhatsApp (FILO-37): un contatto per riga. Non hanno
 * uno slice: le tiene la pagina, che le ricarica dopo ogni invio.
 */
export const fetchWhatsAppConversations = createAsyncThunk<
  WhatsAppConversation[],
  void,
  { state: RootState; rejectValue: string }
>('whatsAppSessions/fetchConversations', async (_, { getState, rejectWithValue }) => {
  try {
    return await whatsAppSessionApi.getConversations(requireGroupId(getState));
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Il contatto di una conversazione, coi suoi numeri. Non passa da `fetchPerson`:
 * scriverebbe `currentItem` dello slice dei contatti, che è della scheda.
 */
export const fetchWhatsAppRecipient = createAsyncThunk<
  Person,
  number,
  { rejectValue: string }
>('whatsAppSessions/fetchRecipient', async (personId, { rejectWithValue }) => {
  try {
    return await personApi.getById(personId);
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Ricerca in rubrica per scegliere a chi scrivere. Non passa da `fetchPersons`, che
 * sostituirebbe l'elenco dei contatti con i risultati della ricerca.
 */
export const searchWhatsAppRecipients = createAsyncThunk<
  Person[],
  string,
  { state: RootState; rejectValue: string }
>('whatsAppSessions/searchRecipients', async (search, { getState, rejectWithValue }) => {
  try {
    const page = await personApi.getAll(requireGroupId(getState), { search, pageSize: 10 });
    return page.items;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});
